import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from "react";
import type { ApiClient } from "../api/client";
import type { LoadedOverlay } from "./kmzOverlay";
import {
  describeOverlayError,
  isOverlayFile,
  prepareOverlayFile,
  type PreparedOverlay,
} from "./overlayUpload";

/** What the Overlays menu shows while a file waits for its name. */
export interface PendingOverlay {
  filename: string;
  name: string;
  featureCount: number;
  /** The trimmed name matches one of the team's own overlays: Add becomes Replace. */
  replaces: boolean;
  busy: boolean;
  error: string | null;
}

export interface OverlayManager {
  online: boolean;
  pending: PendingOverlay | null;
  /** Files waiting behind the pending one. */
  queued: number;
  /** A one-line message outside the panel: skipped files, a failed remove. */
  notice: string | null;
  /** Stage picked or dropped files. Non-overlay and unreadable files are
   *  skipped with a notice; the rest queue through the name panel in order. */
  addFiles(files: File[]): void;
  setName(name: string): void;
  /** Upload the pending overlay under its current name. */
  confirm(): Promise<void>;
  cancel(): void;
  remove(id: string): Promise<void>;
  dismissNotice(): void;
}

export interface OverlayManagerInput {
  apiClient: ApiClient;
  team?: string;
  overlays: LoadedOverlay[];
  setOverlays: Dispatch<SetStateAction<LoadedOverlay[]>>;
  setVisibleIds: Dispatch<SetStateAction<Set<string>>>;
  online: boolean;
}

function ownNameMatch(overlays: LoadedOverlay[], name: string): LoadedOverlay | undefined {
  return overlays.find((o) => o.inherited !== true && o.name === name);
}

/**
 * Adding and removing the team's reference overlays from the viewer page
 * (spec 2026-09-09). Owns the queue of prepared files and the one pending
 * the reviewer's name; ViewerPage keeps owning the overlay list and the
 * visible set, which this writes through the setters it is handed.
 */
export function useOverlayManager(input: OverlayManagerInput): OverlayManager {
  const { apiClient, team, overlays, setOverlays, setVisibleIds, online } = input;
  const [pendingFile, setPendingFile] = useState<PreparedOverlay | null>(null);
  const [name, setNameState] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queue, setQueue] = useState<PreparedOverlay[]>([]);
  const [notice, setNotice] = useState<string | null>(null);

  // Pull the next prepared file into the panel whenever it is empty.
  useEffect(() => {
    if (pendingFile !== null || queue.length === 0) return;
    const [next, ...rest] = queue;
    setQueue(rest);
    setPendingFile(next!);
    setNameState(next!.suggestedName);
    setError(null);
  }, [pendingFile, queue]);

  const addFiles = useCallback((files: File[]) => {
    if (!online) return;
    void (async () => {
      const skipped: string[] = [];
      const prepared: PreparedOverlay[] = [];
      for (const file of files) {
        if (!isOverlayFile(file.name)) { skipped.push(file.name); continue; }
        const p = await prepareOverlayFile(file);
        if (p === null) skipped.push(file.name);
        else prepared.push(p);
      }
      if (skipped.length > 0) {
        setNotice(`Couldn't read ${skipped.join(", ")} as KML or KMZ.`);
      }
      if (prepared.length > 0) setQueue((q) => [...q, ...prepared]);
    })();
  }, [online]);

  const confirm = useCallback(async () => {
    if (pendingFile === null || busy) return;
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setError("Give it a name first.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const entry = await apiClient.addOverlay(pendingFile.kmz, trimmed, team);
      const loaded: LoadedOverlay = { id: entry.id, name: entry.name, fc: pendingFile.fc };
      setOverlays((prev) => [...prev.filter((o) => o.id !== entry.id), loaded]);
      setVisibleIds((prev) => new Set(prev).add(entry.id));
      setPendingFile(null);
    } catch (err) {
      setError(describeOverlayError(err));
    } finally {
      setBusy(false);
    }
  }, [apiClient, busy, name, pendingFile, setOverlays, setVisibleIds, team]);

  const cancel = useCallback(() => {
    if (busy) return;
    setPendingFile(null);
    setError(null);
  }, [busy]);

  const remove = useCallback(async (id: string) => {
    try {
      await apiClient.removeOverlay(id, team);
      setOverlays((prev) => prev.filter((o) => o.id !== id));
      setVisibleIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) {
      setNotice(describeOverlayError(err));
    }
  }, [apiClient, setOverlays, setVisibleIds, team]);

  const pending: PendingOverlay | null = pendingFile === null ? null : {
    filename: pendingFile.filename,
    name,
    featureCount: pendingFile.featureCount,
    replaces: ownNameMatch(overlays, name.trim()) !== undefined,
    busy,
    error,
  };

  return {
    online,
    pending,
    queued: queue.length,
    notice,
    addFiles,
    setName: setNameState,
    confirm,
    cancel,
    remove,
    dismissNotice: () => setNotice(null),
  };
}
